import { useMemo } from "react";

import { useSelector } from "react-redux";

import { useHasSideLoadedBusinessPermission } from "hooks/useHasPermission";
import { getBusinessRegistrations, getProperties } from "selectors";
import {
  businessToMatchCandidate,
  MatchCandidate,
  selectBusinessMatcher,
  selectSideLoadedMatcher,
  sideLoadedToMatchCandidate,
} from "selectors/temporaryBusinesses";

// All match candidates for a temporary Business - existing Businesses first,
// then side-loaded (legacy) records for users who can see them.
export function useTemporaryBusinessMatcher(): (
  temporaryBusiness: Business,
) => MatchCandidate[] {
  const matchBusiness = useSelector(selectBusinessMatcher);
  const matchSideLoaded = useSelector(selectSideLoadedMatcher);
  const propertyByIdLookup = useSelector(getProperties);
  const registrationsById = useSelector(getBusinessRegistrations);
  const hasSideLoadedPermission = useHasSideLoadedBusinessPermission();

  return useMemo(() => {
    const cache = new WeakMap<Business, MatchCandidate[]>();
    return temporaryBusiness => {
      const cached = cache.get(temporaryBusiness);
      if (cached) {
        return cached;
      }
      const candidates = [
        ...matchBusiness(temporaryBusiness).map(({ business, matchedFields }) =>
          businessToMatchCandidate(
            business,
            matchedFields,
            propertyByIdLookup,
            registrationsById,
          ),
        ),
        ...(hasSideLoadedPermission
          ? matchSideLoaded(temporaryBusiness).map(
              ({ record, matchedFields }) =>
                sideLoadedToMatchCandidate(record, matchedFields),
            )
          : []),
      ];
      cache.set(temporaryBusiness, candidates);
      return candidates;
    };
  }, [
    matchBusiness,
    matchSideLoaded,
    propertyByIdLookup,
    registrationsById,
    hasSideLoadedPermission,
  ]);
}
